/**
 * Subtle scroll-in reveals for page blocks (same language as scroll-icons: y + opacity, power2.out).
 * Each block roots on its BEM section; no-ops if elements are missing.
 */
import gsap from "gsap";

const START = "top 88%";
const DURATION = 0.65;
const EASE = "power2.out";
const STAGGER = 0.1;
const FROM = { opacity: 0, y: 28 };

function reveal(trigger, elements, opts = {}) {
	const els = (Array.isArray(elements) ? elements : [elements]).filter(Boolean);
	if (!els.length || !trigger) return;

	gsap.set(els, { ...FROM, ...opts.from });
	gsap.to(els, {
		opacity: 1,
		y: 0,
		x: 0,
		duration: opts.duration ?? DURATION,
		ease: EASE,
		stagger: opts.stagger ?? STAGGER,
		scrollTrigger: {
			trigger,
			start: opts.start ?? START,
			toggleActions: "play none none none",
		},
	});
}

export function initBlockScrollReveals() {
	document.querySelectorAll(".heading-text").forEach((section) => {
		reveal(section, [
			section.querySelector(".heading-text__heading-wrap"),
			section.querySelector(".heading-text__content"),
		]);
	});

	document.querySelectorAll(".simple-content").forEach((section) => {
		const wrap = section.querySelector(".simple-content__wrap");
		if (wrap) reveal(section, [wrap], { stagger: 0 });
	});

	document.querySelectorAll(".cta-banner").forEach((section) => {
		reveal(section, [
			section.querySelector(".cta-banner__title"),
			section.querySelector(".cta-banner__actions"),
		]);
	});

	document.querySelectorAll(".divider-line").forEach((section) => {
		const line = section.querySelector(".divider-line__line");
		if (!line) return;
		gsap.set(line, { scaleX: 0, transformOrigin: "left center" });
		gsap.to(line, {
			scaleX: 1,
			duration: 0.75,
			ease: "power2.out",
			scrollTrigger: {
				trigger: section,
				start: START,
				toggleActions: "play none none none",
			},
		});
	});

	document.querySelectorAll(".gallery-slider").forEach((section) => {
		const slider = section.querySelector(".gallery-slider__slider-wrap");
		if (slider) reveal(section, [slider], { stagger: 0 });
	});

	document.querySelectorAll(".image-text").forEach((section) => {
		reveal(section, [
			section.querySelector(".image-text__media"),
			section.querySelector(".image-text__content"),
		]);
	});

	document.querySelectorAll(".industries-slider").forEach((section) => {
		reveal(section, [
			section.querySelector(".industries-slider__header"),
			section.querySelector(".industries-slider__slider-wrap"),
		]);
	});

	document.querySelectorAll(".blog-slider").forEach((section) => {
		reveal(section, [
			section.querySelector(".blog-slider__header"),
			section.querySelector(".blog-slider__slider-wrap"),
		]);
	});

	document.querySelectorAll(".video-slider").forEach((section) => {
		reveal(section, [
			section.querySelector(".video-slider__header"),
			section.querySelector(".video-slider__slider-wrap"),
		]);
	});

	document.querySelectorAll(".latest-work").forEach((section) => {
		const header = section.querySelector(".latest-work__header");
		const items = section.querySelectorAll(".latest-work__grid .grid-12__item");
		const marquee = section.querySelector(".latest-work__marquee");
		const targets = [header, ...items, marquee].filter(Boolean);
		if (targets.length) {
			reveal(section, targets, { stagger: items.length ? 0.08 : STAGGER });
		}
	});

	document.querySelectorAll(".related-work").forEach((section) => {
		reveal(section, [
			section.querySelector(".related-work__header"),
			section.querySelector(".related-work__slider-wrap"),
		]);
	});

	document.querySelectorAll(".case-study-intro").forEach((section) => {
		const isGrid = section.classList.contains("case-study-intro--grid");
		if (isGrid) {
			reveal(section, [
				section.querySelector(".case-study-intro__content-col"),
				section.querySelector(".case-study-intro__media-grid"),
			]);
		} else {
			reveal(section, [
				section.querySelector(".case-study-intro__top"),
				section.querySelector(".case-study-intro__slider-wrap"),
			]);
		}
	});

	document.querySelectorAll(".testimonial").forEach((section) => {
		reveal(section, [
			section.querySelector(".testimonial__title"),
			section.querySelector(".testimonial__card"),
		]);
	});

	document.querySelectorAll(".team-grid").forEach((section) => {
		const cells = section.querySelectorAll(".team-grid__cell");
		if (!cells.length) return;
		gsap.set(cells, FROM);
		gsap.to(cells, {
			opacity: 1,
			y: 0,
			duration: DURATION,
			ease: EASE,
			stagger: 0.08,
			scrollTrigger: {
				trigger: section,
				start: START,
				toggleActions: "play none none none",
			},
		});
	});

	document.querySelectorAll(".logo-marquee").forEach((section) => {
		reveal(section, [
			section.querySelector(".logo-marquee__title"),
			section.querySelector(".logo-marquee .marquee"),
		]);
	});

	document.querySelectorAll(".marquee-block").forEach((section) => {
		const bar = section.querySelector(".marquee-bar");
		if (bar) reveal(section, [bar], { stagger: 0 });
	});

	document.querySelectorAll(".google-reviews").forEach((section) => {
		reveal(section, [
			section.querySelector(".google-reviews__header"),
			section.querySelector(".google-reviews__slider-wrap"),
		]);
	});

	document.querySelectorAll(".process-steps").forEach((section) => {
		const headerWrap = section.querySelector(".process-steps__header__wrap");
		const headerEls = headerWrap ? [...headerWrap.children] : [];
		const slider = section.querySelector(".process-steps__slider-wrap");
		const targets = [...headerEls, slider].filter(Boolean);
		if (targets.length) reveal(section, targets, { stagger: 0.09 });
	});

	document.querySelectorAll(".central-content").forEach((section) => {
		reveal(section, [
			section.querySelector(".central-content__title"),
			section.querySelector(".central-content__text"),
			section.querySelector(".central-content__actions"),
		]);
	});

	document.querySelectorAll(".contact-form").forEach((section) => {
		reveal(section, [
			section.querySelector(".contact-form__left"),
			section.querySelector(".contact-form__right"),
		]);
	});
}
